/**
 * Detecting whether the site is being viewed inside our own native app.
 *
 * Twinr builds the mobile app by wrapping this very website in a WebView, so
 * without a check like this every in-app user would be nagged to install the
 * app they are already using.
 *
 * Three independent signals, any one of which is enough:
 *
 * 1. User agent token. Twinr's "Custom User Agent" extension (Add-ons →
 *    Extensions, with "Attach" on so the token is appended to the real user
 *    agent rather than replacing it) puts a token of your choosing in the
 *    WebView's user agent. Set the same token in REACT_APP_NATIVE_APP_UA_TOKEN
 *    and the two sides line up. Matching is case-insensitive, and several
 *    tokens can be given as a comma-separated list.
 *
 *    NOTE: Twinr has no default token - this signal does nothing until that
 *    extension is switched on and REACT_APP_NATIVE_APP_UA_TOKEN is set to
 *    whatever token was configured there. The 'twinr' fallback below is a
 *    guess, not something Twinr documents.
 *
 * 2. Twinr's JS bridge. When the bridge is enabled, Twinr injects globals into
 *    the WebView - `twinr_device_info()` and `twinr_push_token_fetch()` are the
 *    documented ones. Both those names and any other global starting with
 *    `twinr` are treated as proof we're inside the app.
 *
 * 3. A `?native_app=1` query parameter, remembered in localStorage afterwards.
 *    Our own manual escape hatch - nothing to do with Twinr - for when neither
 *    of the above is switched on.
 *
 * Note that the bridge globals can land a moment after the first paint, so
 * callers should check late rather than on mount - see AppPromoBanner.
 */

const UA_TOKENS_RAW = process.env.REACT_APP_NATIVE_APP_UA_TOKEN || 'twinr';
const QUERY_PARAM = 'native_app';
const STORAGE_KEY = 'shimmy_native_app';

const isBrowser = typeof window !== 'undefined';

const uaTokens = UA_TOKENS_RAW.split(',')
  .map(token => token.trim().toLowerCase())
  .filter(Boolean);

// localStorage throws in Safari private mode and when cookies are blocked.
const safeStorage = {
  get: key => {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set: (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      // Nothing to do - the flag just won't survive a reload.
    }
  },
};

const hasNativeAppUserAgent = () => {
  const ua = (window.navigator?.userAgent || '').toLowerCase();
  return uaTokens.some(token => ua.includes(token));
};

// The bridge functions Twinr documents by name. We never call them - their
// presence alone is the signal.
const TWINR_BRIDGE_GLOBALS = ['twinr_device_info', 'twinr_push_token_fetch'];

const hasTwinrBridge = () =>
  TWINR_BRIDGE_GLOBALS.some(name => typeof window[name] === 'function') ||
  // Catch-all for the bridge functions we haven't named, since they all share
  // the prefix.
  Object.keys(window).some(key => key.toLowerCase().startsWith('twinr'));

const hasNativeAppQueryParam = () => {
  const value = new URLSearchParams(window.location.search).get(QUERY_PARAM);
  return value != null && !['0', 'false', 'no'].includes(value.toLowerCase());
};

/**
 * Is this page running inside our native (Twinr) app?
 *
 * Browser-only: always returns false during SSR, so anything that depends on it
 * has to be resolved client-side to avoid a hydration mismatch.
 *
 * @returns {boolean} true when the page is inside the native app wrapper
 */
export const isNativeApp = () => {
  if (!isBrowser) {
    return false;
  }
  if (hasNativeAppQueryParam()) {
    safeStorage.set(STORAGE_KEY, 'true');
    return true;
  }
  if (hasNativeAppUserAgent() || hasTwinrBridge()) {
    return true;
  }
  return safeStorage.get(STORAGE_KEY) === 'true';
};
